import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Check,
  Loader2,
  Mail,
  MapPin,
  MessageCircle,
  PartyPopper,
  Phone,
  Sparkles,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SKILL_OPTIONS = ["Beginner", "Intermediate", "Advanced", "Pro"];
const DEFAULT_SLOTS = [
  "8:00 AM – 10:00 AM",
  "10:00 AM – 12:00 PM",
  "12:00 PM – 4:00 PM",
  "4:00 PM – 6:00 PM",
  "6:00 PM – 8:00 PM",
];
const PLAYO_URL = "http://go.playo.app/PLAYOO/k7o3c";
const DEFAULT_EVENT = {
  name: "Baddy Sunday",
  tagline: "Open Sports Day",
  date_label: "Sunday, 27 September 2026",
  hours_label: "8 AM – 8 PM",
  age_min: 12,
  slot_note: "30-minute court slot allotted on the spot within your chosen window.",
};

const inputClass =
  "w-full bg-white text-[#0A0A0A] font-medium text-base p-4 border-2 border-[#0A0A0A] rounded-xl focus:outline-none focus:ring-0 placeholder:text-neutral-400 transition-all";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SignupPage() {
  const [step, setStep] = useState("form"); // form | otp | success
  const [config, setConfig] = useState({
    skill_levels: SKILL_OPTIONS,
    areas: [],
    time_slots: DEFAULT_SLOTS,
    event: DEFAULT_EVENT,
    whatsapp_channel_url: "",
    academy_name: "Ganapathi Badminton Academy",
    academy_location: "Bheema Sandra, Tumkur",
    resend_cooldown_seconds: 60,
    otp_ttl_seconds: 300,
  });

  const [name, setName] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [email, setEmail] = useState("");
  const [age, setAge] = useState("");
  const [slot, setSlot] = useState("");
  const [skill, setSkill] = useState("");
  const [area, setArea] = useState("");
  const [otp, setOtp] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [devOtp, setDevOtp] = useState("");
  const [signup, setSignup] = useState(null);
  const [resendIn, setResendIn] = useState(0);
  const tickRef = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/config`)
      .then((r) => setConfig((c) => ({ ...c, ...r.data })))
      .catch(() => {});
  }, []);

  // Resend cooldown ticker
  useEffect(() => {
    if (resendIn <= 0) {
      clearInterval(tickRef.current);
      return;
    }
    tickRef.current = setInterval(() => {
      setResendIn((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [resendIn]);

  const isValidPhone = /^[6-9]\d{9}$/.test(whatsapp.replace(/\D/g, ""));
  const isValidEmail = EMAIL_RE.test(email.trim());
  const ageNum = parseInt(age, 10);
  const ageMin = config?.event?.age_min ?? 12;
  const isValidAge = Number.isFinite(ageNum) && ageNum >= ageMin && ageNum <= 99;
  const canRequestOtp =
    name.trim().length >= 2 &&
    isValidEmail &&
    isValidPhone &&
    isValidAge &&
    !!slot &&
    !!skill &&
    !!area;

  const handleSendOtp = async (isResend = false) => {
    if (!canRequestOtp) {
      toast.error("Please fill every field first.");
      return;
    }
    setSending(true);
    try {
      const r = await axios.post(`${API}/auth/send-otp`, {
        name: name.trim(),
        email: email.trim().toLowerCase(),
      });
      setDevOtp(r.data.dev_otp || "");
      setResendIn(r.data.resend_cooldown_seconds || 60);
      if (!isResend) setStep("otp");
      toast.success(
        r.data.delivered ? "Code emailed ✉️" : "Code generated",
        {
          description: r.data.delivered
            ? `Check ${email.trim().toLowerCase()}`
            : r.data.dev_otp
            ? `Demo OTP: ${r.data.dev_otp}`
            : undefined,
        }
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429 && typeof detail === "string") {
        const m = detail.match(/(\d+)s/);
        if (m) setResendIn(parseInt(m[1], 10));
      }
      toast.error(detail || "Couldn't send OTP");
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async () => {
    if (otp.length !== 6) {
      toast.error("Enter the 6-digit code");
      return;
    }
    setVerifying(true);
    try {
      const r = await axios.post(`${API}/auth/verify-otp`, {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        otp,
        whatsapp_number: whatsapp,
        age: ageNum,
        preferred_slot: slot,
        skill_level: skill,
        area,
      });
      setSignup(r.data);
      setStep("success");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  const resetAll = () => {
    setStep("form");
    setOtp("");
    setSignup(null);
    setResendIn(0);
  };

  return (
    <div className="min-h-screen w-full bg-[#F4F0EA]">
      <div
        className="mx-auto max-w-md min-h-screen flex flex-col"
        data-testid="signup-container"
      >
        {/* Top marquee */}
        <div className="border-b-2 border-[#0A0A0A] bg-[#0A0A0A] text-[#D4FF00] py-2 overflow-hidden">
          <div className="flex whitespace-nowrap animate-marquee font-display font-extrabold tracking-tight text-sm uppercase">
            {Array.from({ length: 8 }).map((_, i) => (
              <span key={i} className="px-6 flex items-center gap-3">
                <Sparkles size={14} /> 1 FREE SESSION ON SIGNUP
                <span className="opacity-50">×</span>
                GANAPATHI BADMINTON ACADEMY
                <span className="opacity-50">×</span>
              </span>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          {step !== "success" && (
            <motion.div
              key="form-wrap"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="flex-1 px-5 sm:px-7 pt-5 pb-10"
            >
              <Hero event={config.event} />

              <div className="mt-6 space-y-5">
                {/* Name */}
                <Field label="What should we call you?">
                  <input
                    data-testid="signup-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Arjun K"
                    disabled={step === "otp"}
                    className={inputClass}
                  />
                </Field>

                {/* Email — primary verification channel */}
                <Field label="Email (we'll send your code here)">
                  <div className="flex items-stretch border-2 border-[#0A0A0A] rounded-xl overflow-hidden bg-white neo-shadow">
                    <div className="px-4 flex items-center bg-[#D4FF00] border-r-2 border-[#0A0A0A]">
                      <Mail size={18} />
                    </div>
                    <input
                      data-testid="signup-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@gmail.com"
                      disabled={step === "otp"}
                      className="flex-1 p-4 text-base font-medium focus:outline-none disabled:bg-neutral-100"
                    />
                  </div>
                  {email && !isValidEmail && (
                    <p className="text-xs font-bold text-red-600 mt-2">
                      Enter a valid email address
                    </p>
                  )}
                </Field>

                {/* WhatsApp — contact only (no OTP on it) */}
                <Field label="WhatsApp number (for court updates)">
                  <div className="flex items-stretch border-2 border-[#0A0A0A] rounded-xl overflow-hidden bg-white neo-shadow">
                    <div className="px-4 flex items-center font-display font-extrabold bg-[#FF90E8] border-r-2 border-[#0A0A0A]">
                      +91
                    </div>
                    <input
                      data-testid="signup-whatsapp-input"
                      value={whatsapp}
                      onChange={(e) =>
                        setWhatsapp(e.target.value.replace(/\D/g, "").slice(0, 10))
                      }
                      placeholder="98xxxxxxxx"
                      inputMode="numeric"
                      disabled={step === "otp"}
                      className="flex-1 p-4 text-base font-medium focus:outline-none disabled:bg-neutral-100"
                    />
                  </div>
                  {whatsapp && !isValidPhone && (
                    <p className="text-xs font-bold text-red-600 mt-2">
                      Enter a valid 10-digit Indian mobile number
                    </p>
                  )}
                </Field>

                {/* Age (event is 12+) */}
                <Field label={`Age (${ageMin}+ only)`}>
                  <input
                    data-testid="signup-age-input"
                    type="number"
                    inputMode="numeric"
                    min={ageMin}
                    max={99}
                    value={age}
                    onChange={(e) =>
                      setAge(e.target.value.replace(/\D/g, "").slice(0, 2))
                    }
                    placeholder={`e.g. ${ageMin + 3}`}
                    disabled={step === "otp"}
                    className={inputClass}
                  />
                  {age && !isValidAge && (
                    <p className="text-xs font-bold text-red-600 mt-2">
                      Baddy Sunday is for players {ageMin} and above.
                    </p>
                  )}
                </Field>

                {/* Preferred time slot */}
                <Field label="Preferred time slot on 27 Sep">
                  <div
                    className="grid grid-cols-1 sm:grid-cols-2 gap-2"
                    data-testid="slot-chip-group"
                  >
                    {(config.time_slots || DEFAULT_SLOTS).map((s) => {
                      const active = slot === s;
                      return (
                        <button
                          type="button"
                          key={s}
                          data-testid={`slot-chip-${s.replace(/[^0-9apAPM]/g, "").toLowerCase()}`}
                          onClick={() => setSlot(s)}
                          disabled={step === "otp"}
                          className={[
                            "border-2 border-[#0A0A0A] rounded-xl py-3 px-3 font-display font-extrabold uppercase tracking-tight text-[13px] transition-all text-left",
                            active
                              ? "bg-[#D4FF00] text-[#0A0A0A] neo-shadow"
                              : "bg-white text-[#0A0A0A] hover:neo-shadow hover:-translate-y-0.5",
                          ].join(" ")}
                        >
                          {s}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[11px] font-medium text-neutral-600 mt-2">
                    A 30-minute court slot is allotted on the spot within your window.
                  </p>
                </Field>

                {/* Skill */}
                <Field label="Your badminton vibe">
                  <div
                    className="grid grid-cols-2 gap-3"
                    data-testid="skill-chip-group"
                  >
                    {(config.skill_levels || SKILL_OPTIONS).map((s) => {
                      const active = skill === s;
                      return (
                        <button
                          type="button"
                          key={s}
                          data-testid={`skill-chip-${s.toLowerCase()}`}
                          onClick={() => setSkill(s)}
                          disabled={step === "otp"}
                          className={[
                            "border-2 border-[#0A0A0A] rounded-xl py-3 px-3 font-display font-extrabold uppercase tracking-tight text-sm transition-all",
                            active
                              ? "bg-[#0051FF] text-white neo-shadow"
                              : "bg-white text-[#0A0A0A] hover:neo-shadow hover:-translate-y-0.5",
                          ].join(" ")}
                        >
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </Field>

                {/* Area */}
                <Field label="Area of living">
                  <Select
                    value={area}
                    onValueChange={setArea}
                    disabled={step === "otp"}
                  >
                    <SelectTrigger
                      data-testid="area-select"
                      className="w-full bg-white border-2 border-[#0A0A0A] rounded-xl p-4 h-auto neo-shadow font-medium text-base data-[placeholder]:text-neutral-400 focus:ring-0 focus:ring-offset-0"
                    >
                      <SelectValue placeholder="Pick your locality" />
                    </SelectTrigger>
                    <SelectContent className="border-2 border-[#0A0A0A] rounded-xl neo-shadow bg-white">
                      {(config.areas || []).map((a) => (
                        <SelectItem
                          key={a}
                          value={a}
                          data-testid={`area-option-${a}`}
                          className="font-medium focus:bg-[#D4FF00] rounded-md"
                        >
                          {a}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                {/* OTP step */}
                {step === "otp" && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-[#FF90E8] border-2 border-[#0A0A0A] rounded-2xl p-5 neo-shadow"
                    data-testid="otp-panel"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Mail size={18} />
                      <p className="font-display font-extrabold uppercase tracking-tight text-sm">
                        Code emailed to {email}
                      </p>
                    </div>
                    <p className="text-xs font-medium text-[#0A0A0A]/80 mb-3">
                      Enter the 6-digit code (valid for 5 minutes).
                    </p>
                    <InputOTP
                      maxLength={6}
                      value={otp}
                      onChange={setOtp}
                      data-testid="otp-input-field"
                      containerClassName="justify-between"
                    >
                      <InputOTPGroup className="gap-2 w-full justify-between">
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                          <InputOTPSlot
                            key={i}
                            index={i}
                            className="w-11 h-14 rounded-xl border-2 border-[#0A0A0A] bg-white text-xl font-display font-extrabold"
                          />
                        ))}
                      </InputOTPGroup>
                    </InputOTP>
                    {devOtp && (
                      <p className="text-xs mt-3 font-bold text-[#0A0A0A]/80">
                        Demo OTP (SMTP not configured):{" "}
                        <span
                          className="bg-[#0A0A0A] text-[#D4FF00] px-2 py-0.5 rounded font-mono"
                          data-testid="dev-otp-display"
                        >
                          {devOtp}
                        </span>
                      </p>
                    )}
                    <div className="mt-3 flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => handleSendOtp(true)}
                        disabled={resendIn > 0 || sending}
                        className="text-xs font-bold underline underline-offset-4 disabled:no-underline disabled:opacity-50"
                        data-testid="resend-otp-button"
                      >
                        {resendIn > 0
                          ? `Resend in ${resendIn}s`
                          : "Resend code"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setStep("form")}
                        className="text-xs font-bold underline underline-offset-4"
                        data-testid="edit-details-button"
                      >
                        Edit details
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* CTA */}
                {step === "form" ? (
                  <button
                    type="button"
                    data-testid="send-otp-button"
                    onClick={() => handleSendOtp(false)}
                    disabled={!canRequestOtp || sending}
                    className="w-full bg-[#D4FF00] text-[#0A0A0A] font-display font-black text-base py-4 px-6 border-2 border-[#0A0A0A] rounded-xl neo-shadow neo-shadow-hover uppercase flex justify-center items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {sending ? (
                      <Loader2 className="animate-spin" size={18} />
                    ) : (
                      <ShieldCheck size={18} />
                    )}
                    Reserve My Spot
                    <ArrowRight size={18} />
                  </button>
                ) : (
                  <button
                    type="button"
                    data-testid="verify-otp-button"
                    onClick={handleVerify}
                    disabled={otp.length !== 6 || verifying}
                    className="w-full bg-[#0051FF] text-white font-display font-black text-base py-4 px-6 border-2 border-[#0A0A0A] rounded-xl neo-shadow neo-shadow-hover uppercase flex justify-center items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {verifying ? (
                      <Loader2 className="animate-spin" size={18} />
                    ) : (
                      <Check size={18} />
                    )}
                    Verify & Confirm Baddy Sunday
                  </button>
                )}

                <p className="text-xs text-center text-neutral-600 pt-1">
                  By signing up you'll get updates from{" "}
                  <span className="font-bold">{config.academy_name}</span>.
                </p>
              </div>
            </motion.div>
          )}

          {step === "success" && (
            <SuccessScreen
              key="success"
              signup={signup}
              channelUrl={
                signup?.whatsapp_channel_url || config.whatsapp_channel_url
              }
              onReset={resetAll}
            />
          )}
        </AnimatePresence>

        <Footer />
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-display font-extrabold uppercase tracking-[0.12em] mb-2 text-[#0A0A0A]">
        {label}
      </span>
      {children}
    </label>
  );
}

function Hero({ event }) {
  const ev = event || DEFAULT_EVENT;
  return (
    <div className="pt-6">
      <div className="flex items-center gap-3 mb-4">
        <img
          src="/brand/logo.png"
          alt="Ganapathi Badminton Academy"
          className="w-14 h-14 rounded-xl border-2 border-[#0A0A0A] bg-white p-0.5 neo-shadow-sm object-contain"
          data-testid="brand-logo"
        />
        <div className="flex flex-col gap-1">
          <span className="inline-flex items-center gap-1 bg-[#0A0A0A] text-[#D4FF00] border-2 border-[#0A0A0A] rounded-full px-2.5 py-0.5 text-[10px] font-display font-extrabold uppercase tracking-wider">
            <span className="w-1.5 h-1.5 rounded-full bg-[#D4FF00]" />
            Open Sports Day · {ev.age_min}+
          </span>
          <span className="inline-flex items-center gap-1 bg-[#FF90E8] border-2 border-[#0A0A0A] rounded-full px-2.5 py-0.5 text-[10px] font-display font-extrabold uppercase tracking-wider">
            Bheema Sandra · Tumkur
          </span>
        </div>
      </div>
      <h1 className="font-display font-black uppercase leading-[0.9] tracking-tighter text-[46px] sm:text-[56px] text-[#0A0A0A]">
        Baddy<br />
        <span className="bg-[#D4FF00] px-2 -mx-1 inline-block tilt-left border-2 border-[#0A0A0A] neo-shadow-sm">
          Sunday.
        </span>
      </h1>
      <p className="mt-4 text-[15px] font-medium text-neutral-700 max-w-[30ch]">
        Open sports day at Ganapathi Badminton Academy —{" "}
        <span className="bg-[#0051FF] text-white px-1.5 font-bold">
          {ev.date_label}
        </span>{" "}
        · {ev.hours_label}. Students {ev.age_min}+ only. Pick your window, we
        allot a 30-min court slot on the spot.
      </p>

      <div className="mt-5 grid grid-cols-3 gap-2">
        <Badge text={ev.date_label.split(",")[1]?.trim() || "Sep 27"} color="#D4FF00" />
        <Badge text={ev.hours_label} color="#FF90E8" />
        <Badge text={`${ev.age_min}+ only`} color="#FFFFFF" />
      </div>

      <PlayoBanner />
    </div>
  );
}

function PlayoBanner() {
  return (
    <a
      href={PLAYO_URL}
      target="_blank"
      rel="noreferrer"
      data-testid="playo-banner"
      className="mt-4 w-full flex items-center justify-between gap-3 bg-[#0051FF] text-white border-2 border-[#0A0A0A] rounded-2xl px-4 py-3 neo-shadow neo-shadow-hover"
    >
      <div className="flex items-center gap-3">
        <span className="w-9 h-9 rounded-full bg-[#D4FF00] border-2 border-[#0A0A0A] flex items-center justify-center text-[#0A0A0A]">
          <Sparkles size={16} />
        </span>
        <div>
          <p className="text-[10px] font-display font-extrabold uppercase tracking-[0.15em] text-[#D4FF00]">
            Also on
          </p>
          <p className="font-display font-black uppercase leading-none tracking-tighter">
            Playo — book courts anytime
          </p>
        </div>
      </div>
      <ArrowRight size={18} />
    </a>
  );
}

function Badge({ text, color }) {
  return (
    <div
      className="border-2 border-[#0A0A0A] rounded-xl px-2 py-2 text-center text-[11px] font-display font-extrabold uppercase tracking-tight neo-shadow-sm"
      style={{ backgroundColor: color }}
    >
      {text}
    </div>
  );
}

function SuccessScreen({ signup, channelUrl, onReset }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex-1 px-5 sm:px-7 pt-8 pb-10 bg-[#D4FF00]"
      data-testid="signup-success"
    >
      <div className="flex items-center gap-2">
        <PartyPopper />
        <span className="font-display font-extrabold uppercase text-xs tracking-wider">
          You're in
        </span>
      </div>
      <h2 className="mt-3 font-display font-black uppercase leading-[0.88] tracking-tighter text-[44px] sm:text-5xl">
        Baddy<br />
        Sunday<br />
        <span className="bg-[#0A0A0A] text-[#D4FF00] px-2 -mx-1 inline-block tilt-right border-2 border-[#0A0A0A]">
          Locked.
        </span>
      </h2>

      <div className="mt-6 bg-white border-2 border-[#0A0A0A] rounded-2xl p-5 neo-shadow">
        <p className="text-sm font-bold uppercase tracking-wider">Booked for</p>
        <p className="font-display font-black text-2xl mt-1">{signup?.name}</p>
        <p className="text-sm font-medium mt-1 break-all">{signup?.email}</p>
        <p className="text-sm font-medium mt-1">
          +{signup?.whatsapp_number} · Age {signup?.age} · {signup?.skill_level} ·{" "}
          {signup?.area}
        </p>
        <div className="mt-3 bg-[#F4F0EA] border-2 border-[#0A0A0A] rounded-xl p-3">
          <p className="text-[11px] font-display font-extrabold uppercase tracking-wider text-neutral-600">
            Sunday, 27 Sep 2026 · your window
          </p>
          <p
            className="font-display font-black text-lg mt-0.5"
            data-testid="success-slot-value"
          >
            {signup?.preferred_slot}
          </p>
        </div>
        <div className="mt-4 border-t-2 border-dashed border-[#0A0A0A] pt-4">
          <p className="text-sm font-medium leading-relaxed">
            Check your inbox — we've emailed your{" "}
            <span className="bg-[#FF90E8] px-1 font-bold">Baddy Sunday</span>{" "}
            confirmation. Reach the venue 10 minutes before your window; a
            30-minute court will be allotted on the spot.
          </p>
        </div>
      </div>

      <a
        href={channelUrl || "#"}
        target="_blank"
        rel="noreferrer"
        data-testid="join-channel-button"
        className="mt-5 w-full bg-[#0051FF] text-white font-display font-black text-base py-4 px-6 border-2 border-[#0A0A0A] rounded-xl neo-shadow neo-shadow-hover uppercase flex justify-center items-center gap-2"
      >
        <MessageCircle size={18} /> Join WhatsApp Channel
      </a>

      <button
        type="button"
        onClick={onReset}
        data-testid="signup-another-button"
        className="mt-3 w-full bg-white text-[#0A0A0A] font-display font-extrabold text-sm py-3 px-6 border-2 border-[#0A0A0A] rounded-xl neo-shadow-sm hover:neo-shadow uppercase"
      >
        Sign up another player
      </button>
    </motion.div>
  );
}

function Footer() {
  return (
    <div className="mt-4 border-t-2 border-[#0A0A0A] bg-[#0A0A0A] text-neutral-300 px-5 py-6">
      <div className="flex items-center gap-3">
        <img
          src="/brand/logo.png"
          alt="Ganapathi Badminton Academy"
          className="w-10 h-10 rounded-lg bg-white p-0.5 object-contain"
        />
        <div>
          <p className="font-display font-extrabold uppercase tracking-wider text-white text-sm">
            Ganapathi Badminton
          </p>
          <p className="text-[11px] text-neutral-400">Bheema Sandra · Tumkur</p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-2">
        <a
          href="tel:+918618825682"
          data-testid="contact-phone-1"
          className="flex items-center justify-between gap-2 bg-[#111] hover:bg-[#1a1a1a] rounded-xl px-3 py-2 border border-neutral-800 text-sm"
        >
          <span className="flex items-center gap-2">
            <Phone size={14} className="text-[#D4FF00]" />
            <span className="font-mono text-white">+91 86188 25682</span>
          </span>
          <span className="text-[10px] font-display font-extrabold uppercase text-neutral-400">
            Call
          </span>
        </a>
        <a
          href="mailto:gbasportstumkur@gmail.com"
          data-testid="contact-email"
          className="flex items-center justify-between gap-2 bg-[#111] hover:bg-[#1a1a1a] rounded-xl px-3 py-2 border border-neutral-800 text-sm"
        >
          <span className="flex items-center gap-2 min-w-0">
            <Mail size={14} className="text-[#D4FF00] shrink-0" />
            <span className="font-mono text-white truncate">gbasportstumkur@gmail.com</span>
          </span>
          <span className="text-[10px] font-display font-extrabold uppercase text-neutral-400">
            Email
          </span>
        </a>
        <a
          href="https://maps.app.goo.gl/bNPLaeMm2MmhzbVM8"
          target="_blank"
          rel="noreferrer"
          data-testid="map-link"
          className="flex items-center justify-between gap-2 bg-[#111] hover:bg-[#1a1a1a] rounded-xl px-3 py-2 border border-neutral-800 text-sm"
        >
          <span className="flex items-center gap-2">
            <MapPin size={14} className="text-[#FF90E8]" />
            <span className="text-white">Find us on Google Maps</span>
          </span>
          <ArrowRight size={14} className="text-neutral-400" />
        </a>
        <a
          href={PLAYO_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="footer-playo-link"
          className="flex items-center justify-between gap-2 bg-[#0051FF] hover:bg-[#0044dd] rounded-xl px-3 py-2 border border-neutral-800 text-sm"
        >
          <span className="flex items-center gap-2">
            <Sparkles size={14} className="text-[#D4FF00]" />
            <span className="text-white font-bold">Book courts on Playo</span>
          </span>
          <ArrowRight size={14} className="text-white" />
        </a>
      </div>

      <p className="mt-4 text-[11px] text-neutral-500">
        © {new Date().getFullYear()} Ganapathi Badminton Academy · All rights reserved.
      </p>
      <p data-testid="footer-legal-name" className="mt-1 text-[11px] text-neutral-500">
        Operated by <span className="text-neutral-300 font-semibold">TUMAKURU CHANDRASHEKAR GNANESH</span>
      </p>
    </div>
  );
}
